import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Compass, BookOpen, Tent, ClipboardCheck, Calculator, FileText, ShieldCheck } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { IMAGES } from "@/data/site";
import { RURAL_INTRO, OPPORTUNITIES, RURAL_CTAS, RURAL_BRAND_LINE, RURAL_REVIEW_DATE } from "@/data/rural";

const TOOLS = [
  { to: "/rural/explorer", icon: Compass, name: "Opportunity Explorer", desc: "Answer ten questions, get a personalised summary of what to investigate.", testid: "tool-card-explorer" },
  { to: "/rural/checklist", icon: ClipboardCheck, name: "Hipcamp Readiness Checklist", desc: "Printable checklist with progress tracking — mandatory vs recommended.", testid: "tool-card-checklist" },
  { to: "/rural/estimator", icon: Calculator, name: "Rural Income Estimator", desc: "Indicative gross revenue, expenses and net operating income.", testid: "tool-card-estimator" },
  { to: "/rural/guest-pack", icon: FileText, name: "Guest Information Pack", desc: "Generate a premium, personalised pre-arrival pack for your guests.", testid: "tool-card-guestpack" },
  { to: "/rural/resources", icon: ShieldCheck, name: "Safety & Resource Hub", desc: "Official RFS, SES, planning, biosecurity and tax links.", testid: "tool-card-resources" },
];

const GUIDES = [
  { to: "/rural/agritourism", icon: BookOpen, name: "Understanding Agritourism Without the Planning Jargon", desc: "The December 2022 reforms, the three categories and the three approval pathways.", testid: "guide-card-agritourism" },
  { to: "/rural/hipcamp", icon: Tent, name: "Your Land. Your Campsite. Your Opportunity.", desc: "The five-step beginner's guide to hosting campers through Hipcamp.", testid: "guide-card-hipcamp" },
  { to: "/rural/protect", icon: ShieldCheck, name: "Earn From Your Land. Care for What Makes It Special.", desc: "Bushfire, flood, biosecurity and looking after neighbours and wildlife.", testid: "guide-card-protect" },
];

const RuralHome = () => (
  <div data-testid="rural-page">
    <Seo
      title="Rural Land — Unlock Your Property's Potential"
      description="Help for NSW rural landowners to discover legal income streams — farm camping, glamping, farm gate sales, agistment and conservation — with the planning rules in plain English."
    />
    <section className="bg-sand-light/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <FadeIn>
            <Eyebrow>{RURAL_INTRO.eyebrow}</Eyebrow>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl text-teal tracking-tight leading-[1.05]">
              {RURAL_INTRO.title}
            </h1>
            <p className="mt-4 text-base md:text-lg font-medium text-charcoal/80">{RURAL_INTRO.tagline}</p>
            <p className="mt-6 font-serif text-xl sm:text-2xl text-teal leading-relaxed">{RURAL_INTRO.lead}</p>
            {RURAL_INTRO.body.map((p, i) => (
              <p key={i} className="mt-4 text-charcoal/75 leading-relaxed">{p}</p>
            ))}
            <p className="mt-5 font-serif text-lg text-teal italic">{RURAL_INTRO.closing}</p>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="mt-8 flex flex-wrap gap-3">
              {RURAL_CTAS.slice(0, 2).map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  data-testid={c.testid}
                  className="inline-flex items-center gap-2 bg-teal text-ivory text-sm font-medium px-6 py-3 rounded-full hover:bg-teal-dark transition-colors"
                >
                  {c.label} <ArrowRight className="w-4 h-4" />
                </Link>
              ))}
              <Link
                to="/book"
                data-testid="rural-cta-book"
                className="inline-flex items-center gap-2 border border-teal text-teal text-sm font-medium px-6 py-3 rounded-full hover:bg-teal hover:text-ivory transition-colors"
              >
                Speak to a Planner <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </FadeIn>
        </div>
        <FadeIn delay={0.15} className="lg:col-span-5">
          <img
            src={IMAGES.rural_homestead}
            alt="Regional NSW rural farmland homestead with rolling golden pastures"
            className="w-full aspect-[4/3] object-cover border border-cloud"
            loading="lazy"
          />
          <p className="mt-3 text-xs text-charcoal/50 font-mono uppercase tracking-[0.14em]">
            Content reviewed {RURAL_REVIEW_DATE} · Sources linked on every page
          </p>
        </FadeIn>
      </div>
    </section>

    <section className="py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>Section 1</Eyebrow>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-teal tracking-tight">
            Explore your land's income opportunities
          </h2>
          <p className="mt-3 max-w-2xl text-charcoal/70 leading-relaxed">
            Eight genuine ways NSW rural land can earn — each with the planning reality attached.
            Owning rural land does not automatically permit camping, tourist accommodation or
            commercial activities; each guide shows you what to check first.
          </p>
        </FadeIn>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="opportunity-grid">
          {OPPORTUNITIES.map((o, i) => (
            <FadeIn key={o.slug} delay={i * 0.04}>
              <Link
                to={`/rural/opportunities/${o.slug}`}
                data-testid={`opportunity-card-${o.slug}`}
                className="group flex flex-col h-full bg-white border border-cloud p-6 hover:border-teal hover:-translate-y-1 transition-all duration-300"
              >
                <span className="text-xs font-mono text-sand">0{i + 1}</span>
                <h3 className="mt-3 font-serif text-xl text-teal leading-snug">{o.title}</h3>
                <p className="mt-2 text-sm text-charcoal/70 leading-relaxed flex-1">{o.tagline}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.14em] text-teal group-hover:gap-2.5 transition-all">
                  Explore This Opportunity <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 lg:py-20 bg-sand-light/40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>The Guides</Eyebrow>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-teal tracking-tight">
            Understand the rules before you act
          </h2>
        </FadeIn>
        <div className="mt-10 grid md:grid-cols-3 gap-5" data-testid="rural-guides">
          {GUIDES.map((g, i) => (
            <FadeIn key={g.to} delay={i * 0.05}>
              <Link
                to={g.to}
                data-testid={g.testid}
                className="group flex flex-col h-full bg-white border border-cloud p-7 hover:border-teal transition-colors"
              >
                <g.icon className="w-6 h-6 text-teal/60" />
                <h3 className="mt-4 font-serif text-lg text-teal leading-snug">{g.name}</h3>
                <p className="mt-2 text-sm text-charcoal/70 leading-relaxed flex-1">{g.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.14em] text-teal">
                  Read the guide <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="py-16 lg:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>Interactive Tools</Eyebrow>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-teal tracking-tight">
            Practical tools, free to use
          </h2>
          <p className="mt-3 max-w-2xl text-charcoal/70 leading-relaxed">
            Not just information to read — things you can use today, from a readiness checklist to a
            personalised guest information pack.
          </p>
        </FadeIn>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="rural-tools">
          {TOOLS.map((t, i) => (
            <FadeIn key={t.to} delay={i * 0.04}>
              <Link
                to={t.to}
                data-testid={t.testid}
                className="group flex flex-col h-full bg-white border border-cloud p-5 hover:border-teal hover:-translate-y-0.5 transition-all duration-300"
              >
                <t.icon className="w-5 h-5 text-teal/60" />
                <h3 className="mt-3 font-serif text-base text-teal leading-snug">{t.name}</h3>
                <p className="mt-1.5 text-xs text-charcoal/65 leading-relaxed flex-1">{t.desc}</p>
              </Link>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-teal text-ivory">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 text-center">
        <FadeIn>
          <p className="font-serif text-2xl sm:text-3xl leading-relaxed">{RURAL_BRAND_LINE}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {RURAL_CTAS.map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="text-xs font-mono uppercase tracking-[0.14em] border border-ivory/40 px-4 py-2.5 rounded-full hover:bg-ivory hover:text-teal transition-colors"
              >
                {c.label}
              </Link>
            ))}
          </div>
          <p className="mt-10 text-xs font-mono uppercase tracking-[0.2em] text-sand">
            Understand your land. Explore its potential. Plan before you act.
          </p>
        </FadeIn>
      </div>
    </section>
  </div>
);

export default RuralHome;
